import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Tag,
  Row,
  Col,
  Space,
  Input,
  Modal,
  Select,
  Tooltip,
  message,
  Spin,
  Avatar,
  Typography,
  Badge,
  Dropdown,
  Menu
} from "antd";
import {
  SearchOutlined,
  UserAddOutlined,
  UserOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  EditOutlined,
  FilterOutlined,
  LeftOutlined,
  RightOutlined
} from "@ant-design/icons";

import { useNavigate } from "react-router-dom";
import MarketPlaceService from "../../../utils/api/marketplace";
import UserService from "../../../utils/api/users";
import "../../../scss/supplierStyle.css";

const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const SupplierList = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  let history = useNavigate();

  const [initialized, setInitialized] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [responsibles, setResponsibles] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedResponsible, setSelectedResponsible] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingAssign, setLoadingAssign] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const [showInactive, setShowInactive] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");

  // Filtrar proveedores activos e inactivos
  const activeSuppliers = suppliers.filter((supplier) => supplier.status);
  const inactiveSuppliers = suppliers.filter((supplier) => !supplier.status);

  // Datos actuales para mostrar en la tabla
  const currentSuppliers = showInactive ? inactiveSuppliers : activeSuppliers;
  const displayedSuppliers = filteredSuppliers.length > 0 ?
    filteredSuppliers.filter(supplier => showInactive ? !supplier.status : supplier.status) :
    currentSuppliers;

  useEffect(() => {
    if (!initialized) {
      setLoading(true);

      Promise.all([
        UserService.GetAllSuppliers().then((resp) => {
          setSuppliers(resp.data);
          setFilteredSuppliers(resp.data);
        }),
        UserService.GetUsersByRoles([3, 5]).then((resp) => setResponsibles(resp.data)),
        MarketPlaceService.getAllVendors().then((resp) => {
          setVendors(resp.data);
        }),
      ])
        .catch((error) => {
          message.error("Error al cargar datos iniciales");
        })
        .finally(() => {
          setLoading(false);
          setInitialized(true);
        });
    }
  }, [initialized]);

  const handleSupplierChange = (value) => {
    const supplier = suppliers.find((sup) => sup.id === value);
    setSelectedSupplier(supplier);
  };

  const handleResponsibleChange = (value) => {
    const responsible = responsibles.find((u) => u.id === value);
    setSelectedResponsible(responsible);
  };

  const handleAssign = () => {
    if (!selectedSupplier || !selectedResponsible) return;

    setLoadingAssign(true);
    const newVendor = {
      admin_id: user.id,
      supplier_id: selectedSupplier.id,
      responsible_id: selectedResponsible.id,
    };

    MarketPlaceService.addVendor(newVendor)
      .then(() => {
        message.success("Proveedor asignado exitosamente.");
        MarketPlaceService.getAllVendors()
          .then((resp) => setVendors(resp.data))
          .catch(() => message.error("Error al actualizar lista"));
      })
      .catch((error) => {
        message.error("Error al asignar proveedor.");
      })
      .finally(() => setLoadingAssign(false));

    setIsModalOpen(false);
    setSelectedSupplier(null);
    setSelectedResponsible(null);
  };

  const handleCancel = () => {
    setSelectedSupplier(null);
    setSelectedResponsible(null);
    setIsModalOpen(false);
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    if (!value.trim()) {
      setFilteredSuppliers(suppliers);
      return;
    }

    const filtered = suppliers.filter((sup) =>
      sup.bussiness?.toLowerCase().includes(value.toLowerCase()) ||
      sup.name?.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredSuppliers(filtered);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch(searchTerm);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilteredSuppliers(suppliers);
  };

  const handleDeleteVendor = (vendorId) => {
    setLoadingDelete(true);
    MarketPlaceService.deleteVendor(vendorId)
      .then(() => {
        message.success("Vendedor eliminado correctamente.");
        MarketPlaceService.getAllVendors()
          .then((resp) => setVendors(resp.data))
          .catch(() => message.error("Error al actualizar lista"));
      })
      .catch((error) => {
        message.error("Error al eliminar vendedor.");
      })
      .finally(() => setLoadingDelete(false));
  };

  const getResponsibleForSupplier = (supplierId) => {
    const vendor = vendors.find(v => v.vendor?.supplier_id === supplierId);
    if (!vendor) return null;

    return responsibles.find(r => r.id === vendor.vendor?.responsible_id);
  };

  const getVendorIdForSupplier = (supplierId) => {
    const vendor = vendors.find(v => v.vendor?.supplier_id === supplierId);
    return vendor?.vendor_id;
  };

  const formatAddress = (supplier) => {
    const parts = [
      supplier.street,
      supplier.ext_num,
      supplier.int_num && supplier.int_num !== "undefined" ? `Int. ${supplier.int_num}` : null,
      supplier.colony,
      supplier.city,
      supplier.state
    ].filter(Boolean);

    return parts.join(", ");
  };

  const handleClearAllFilters = () => {
    handleClearSearch();
    setShowInactive(false);
  };

  const getStatusBadgeClass = (status) => {
    return status ? "status-badge activo" : "status-badge inactivo";
  };

  const hasActiveFilters = searchTerm.trim() !== "" || showInactive;

  const data = displayedSuppliers.map((supplier) => {
    const responsible = getResponsibleForSupplier(supplier.id);

    return {
      key: supplier.id,
      bussiness: supplier.bussiness,
      name: supplier.name,
      email: supplier.email,
      phone: supplier.phone,
      address: formatAddress(supplier),
      rfc: supplier.rfc,
      status: supplier.status,
      logo: supplier.logo,
      responsibleName: responsible ? responsible.name : "No asignado",
      responsibleEmail: responsible ? responsible.email : "",
      country: supplier.country,
      supplierData: supplier
    };
  });

  const getDisplayRange = () => {
    const start = ((currentPage - 1) * pageSize) + 1;
    const end = Math.min(start + displayedSuppliers.length - 1, displayedSuppliers.length);
    return { start, end };
  };

  const displayRange = getDisplayRange();

  if (loading && suppliers.length === 0) {
    return (
      <div className="admin-suppliers-container">
        <div className="orders-card">
          <div className="loading-container">
            <Spin size="large" />
            <p>Cargando proveedores...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-suppliers-container">
      <div className="orders-card">
        <div className="table-header-section">
          <h1>Gestión de Proveedores</h1>

          <div className="filters-section">
            <div className="search-filter">
              <Input
                className="simple-search"
                placeholder="Buscar por nombre o empresa..."
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyPress={handleSearchKeyPress}
                prefix={<SearchOutlined />}
                size="large"
                allowClear
                onClear={handleClearSearch}
                suffix={
                  <Button
                    type="link"
                    size="small"
                    onClick={() => handleSearch(searchTerm)}
                    loading={loading}
                    disabled={searchTerm.trim() === ""}
                  >
                    Buscar
                  </Button>
                }
                disabled={loading}
              />
            </div>

            <div className="add-user-filter">
              <button className="add-user-button" onClick={showModal} disabled={loading}>
                <UserAddOutlined style={{ marginRight: 8 }} />
                Asignar proveedor
              </button>
            </div>
          </div>
        </div>

        {/* Mostrar filtros activos */}
        {hasActiveFilters && (
          <div className="active-filters-section">
            <span className="filters-label">
              Filtros activos:
            </span>
            <div className="filters-tags">
              {searchTerm.trim() !== "" && (
                <span className="filter-tag search-tag">
                  Búsqueda: "{searchTerm}"
                </span>
              )}
              {showInactive && (
                <span className="filter-tag status-tag">
                  Mostrando: Inactivos
                </span>
              )}
            </div>
            <Button
              type="link"
              size="small"
              onClick={handleClearAllFilters}
              className="clear-filters-button"
            >
              Limpiar todos los filtros
            </Button>
          </div>
        )}

        <div className="table-container">
          <div className="styled-table custom-table">
            <div className="table-header">
              <div className="header-row">
                <div className="header-cell empresa">Empresa</div>
                <div className="header-cell contacto">Contacto</div>
                <div className="header-cell direccion">Dirección</div>
                <div className="header-cell rfc">RFC</div>
                <div className="header-cell responsable">Responsable</div>
                <div className="header-cell estatus">
                  <div className="filter-header" onClick={() => setShowInactive(!showInactive)}>
                    Estatus
                    <FilterOutlined className="filter-icon" />
                    {showInactive && (
                      <Badge
                        dot
                        className="filter-badge"
                      />
                    )}
                  </div>
                </div>
                <div className="header-cell accion">Acciones</div>
              </div>
            </div>

            <div className="table-body">
              {displayedSuppliers.length === 0 ? (
                <div className="empty-state">
                  {loading ? (
                    <Spin size="small" />
                  ) : hasActiveFilters ? (
                    <div>
                      <p>No se encontraron proveedores con los filtros aplicados</p>
                      <Button
                        type="link"
                        onClick={handleClearAllFilters}
                        className="show-all-button"
                      >
                        Mostrar todos los proveedores
                      </Button>
                    </div>
                  ) : (
                    <p>No hay proveedores disponibles</p>
                  )}
                </div>
              ) : (
                data.map(supplier => (
                  <div key={supplier.key} className="table-row">
                    <div className="body-row">
                      <div className="body-cell empresa" data-label="Empresa">
                        <div className="supplier-info">
                          <Avatar
                            size={40}
                            src={supplier.logo}
                            icon={<UserOutlined />}
                            className="supplier-avatar"
                          />
                          <div className="supplier-details">
                            <span className="supplier-business">{supplier.bussiness}</span>
                            <span className="supplier-name">{supplier.name}</span>
                          </div>
                        </div>
                      </div>
                      <div className="body-cell contacto" data-label="Contacto">
                        <div className="contact-info">
                          <span className="contact-email">{supplier.email}</span>
                          <span className="contact-phone">{supplier.phone}</span>
                        </div>
                      </div>
                      <div className="body-cell direccion" data-label="Dirección">
                        <div className="address-info">
                          <span className="address-text">{supplier.address}</span>
                          <span className="address-country">{supplier.country}</span>
                        </div>
                      </div>
                      <div className="body-cell rfc" data-label="RFC">
                        <span className="rfc-text">{supplier.rfc}</span>
                      </div>
                      <div className="body-cell responsable" data-label="Responsable">
                        <div className="responsible-info">
                          <span className="responsible-name">{supplier.responsibleName}</span>
                          <span className="responsible-email">{supplier.responsibleEmail}</span>
                        </div>
                      </div>
                      <div className="body-cell estatus" data-label="Estatus">
                        <span className={getStatusBadgeClass(supplier.status)}>
                          {supplier.status ? "ACTIVO" : "INACTIVO"}
                        </span>
                      </div>
                      <div className="body-cell accion" data-label="Acciones">
                        <Tooltip title="Editar proveedor" placement="left">
                          <button
                            className="edit-icon-button"
                            onClick={() => {/* TODO:Preguntar si se queda o se elimina */ }}
                            disabled={loading}
                          >
                            <EditOutlined />
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {displayedSuppliers.length > 0 && (
          <div className="main-pagination-container">
            <div className="pagination-info">
              <span>
                {hasActiveFilters
                  ? `Mostrando ${displayedSuppliers.length} proveedor${displayedSuppliers.length !== 1 ? 'es' : ''} filtrado${displayedSuppliers.length !== 1 ? 's' : ''}`
                  : `Mostrando proveedores ${displayRange.start}-${displayRange.end} de ${displayedSuppliers.length}`
                }
              </span>
            </div>

            <div className="page-size-selector">
              <span>Mostrar:</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                disabled={loading}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
              <span>por página</span>
            </div>

            <div className="simple-pagination">
              <Button
                icon={<LeftOutlined />}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || loading}
                loading={loading}
                className="pagination-button"
              >
                Anterior
              </Button>

              <div className="current-page-indicator">
                Página {currentPage}
              </div>

              <Button
                icon={<RightOutlined />}
                onClick={() => setCurrentPage(prev => prev + 1)}
                iconPosition="end"
                disabled={currentPage * pageSize >= displayedSuppliers.length || loading}
                loading={loading}
                className="pagination-button"
              >
                Siguiente
              </Button>
            </div>
          </div>
        )}

        {hasActiveFilters && displayedSuppliers.length > 0 && (
          <div className="search-clear-section">
            <Button
              type="link"
              onClick={handleClearAllFilters}
              disabled={loading}
              className="show-all-link"
            >
              Mostrar todos los proveedores
            </Button>
          </div>
        )}
      </div>

      {/* Modal de asignación */}
      <Modal
        title="Asignar nuevo vendedor"
        open={isModalOpen}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancelar
          </Button>,
          <Button
            key="assign"
            type="primary"
            onClick={handleAssign}
            disabled={!selectedSupplier || !selectedResponsible}
            loading={loadingAssign}
          >
            Asignar
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <label><strong>Proveedor:</strong></label>
          <Select
            placeholder="Selecciona un proveedor"
            style={{ width: "100%", marginTop: 8 }}
            onChange={handleSupplierChange}
            value={selectedSupplier?.id}
          >
            {suppliers
              .filter(sup => sup.status)
              .map((sup) => (
                <Option key={sup.id} value={sup.id}>
                  {sup.bussiness} - {sup.name}
                </Option>
              ))}
          </Select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label><strong>Responsable:</strong></label>
          <Select
            placeholder="Selecciona un responsable"
            style={{ width: "100%", marginTop: 8 }}
            onChange={handleResponsibleChange}
            value={selectedResponsible?.id}
          >
            {responsibles.map((user) => (
              <Option key={user.id} value={user.id}>
                {user.name} - {user.email}
              </Option>
            ))}
          </Select>
        </div>

        {selectedSupplier && selectedResponsible && (
          <div className="assignment-summary">
            <Text strong>Resumen de asignación:</Text>
            <div className="summary-details">
              <div><strong>Proveedor:</strong> {selectedSupplier.bussiness}</div>
              <div><strong>Responsable:</strong> {selectedResponsible.name}</div>
              <div><strong>Email:</strong> {selectedResponsible.email}</div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SupplierList;